/* eslint-disable @typescript-eslint/no-explicit-any */

import {AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {EConditionType, ERestriction, IDynamicFormFieldType, IDynamicFormFieldValidator} from '../models/dynamic.model';
import {CustomValidators} from './custom-validators.utils';

export class DynamicFormValidationUtils {
  static buildValidations(field: IDynamicFormFieldType) {
    const validations: ValidatorFn | ValidatorFn[] | null = [];
    const validators = field.validators as IDynamicFormFieldValidator[];
    for (let vindex = 0; vindex < validators.length; vindex++) {
      const validator = validators[parseInt(`${vindex}`)];
      const validation = this.getValidation(validator);
      if (validation) {
        validations.push(validation);
      }
    }
    return validations;
  }

  static getValidation(validation: IDynamicFormFieldValidator) {
    const simpleValidators: Record<string, () => ValidatorFn | null> = {
      [ERestriction.Required]: () => Validators.required,
      [ERestriction.BloodGroup]: () => CustomValidators.bloodGroup(validation.value ?? ''),
      [ERestriction.NumberOfFetusesMulti]: () => CustomValidators.numberOfFetusesMulti(validation.value ?? ''),
      [ERestriction.Email]: () => Validators.email,
      [ERestriction.MaxDate]: () => CustomValidators.maxDate(validation.value ?? new Date(), validation.format),
      [ERestriction.MinDate]: () => CustomValidators.minDate(validation.value ?? new Date(), validation.format),
    };

    const valueBasedValidators: Record<string, () => ValidatorFn | null> = {
      [ERestriction.Max]: () => Validators.max(parseInt(validation.value!)),
      [ERestriction.MaxLength]: () => Validators.maxLength(parseInt(validation.value!)),
      [ERestriction.Min]: () => Validators.min(parseInt(validation.value!)),
      [ERestriction.MinLength]: () => Validators.minLength(parseInt(validation.value!)),
      [ERestriction.Pattern]: () => Validators.pattern(validation.value!.replaceAll('\\\\', '\\')),
    };

    if (simpleValidators[validation.restriction]) {
      return simpleValidators[validation.restriction]();
    }

    if (validation.value && valueBasedValidators[validation.restriction]) {
      return valueBasedValidators[validation.restriction]();
    }

    return null;
  }

  static getRestriction(formTemplate: IDynamicFormFieldType, restrictionType: ERestriction) {
    const field = formTemplate.validators?.find(r => r.restriction === restrictionType);
    return !!field;
  }

  static getRestrictionValue(formTemplate: IDynamicFormFieldType, restrictionType: ERestriction) {
    const field = formTemplate.validators?.find(r => r.restriction === restrictionType);
    if (field?.value) {
      return field.value;
    } else {
      return field?.restriction === ERestriction.MaxDate || field?.restriction === ERestriction.MinDate
        ? new Date()
        : field?.value;
    }
  }

  static updateConditionalValidators(
    dynamicForm: FormGroup | FormArray,
    formTemplateList: IDynamicFormFieldType[],
    index?: number,
    parentHidden: boolean = false
  ): void {
    if (dynamicForm instanceof FormArray) {
      this.updateConditionalValidatorsForArray(dynamicForm, formTemplateList, parentHidden);
      return;
    }

    for (const field of formTemplateList) {
      const nestedFields = this.getNestedFields(field);
      const control = this.getControlByName(dynamicForm, field.name, index);
      const hidden = parentHidden || this.isHidden(field, dynamicForm, formTemplateList, index);

      if (!control) {
        this.updateConditionalValidatorsForNestedFields(dynamicForm, nestedFields, index, hidden);
        continue;
      }

      this.updateControlValidators(control, field, dynamicForm, formTemplateList, index, hidden);
      this.updateConditionalValidatorsForControl(control, nestedFields, hidden);
    }
  }

  private static updateConditionalValidatorsForArray(
    formArray: FormArray,
    formTemplateList: IDynamicFormFieldType[],
    parentHidden: boolean
  ): void {
    formArray.controls.forEach((control, controlIndex) => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.updateConditionalValidators(control, formTemplateList, controlIndex, parentHidden);
      }
    });
  }

  private static updateConditionalValidatorsForNestedFields(
    dynamicForm: FormGroup,
    nestedFields: IDynamicFormFieldType[],
    index: number | undefined,
    hidden: boolean
  ): void {
    if (nestedFields.length === 0) {
      return;
    }

    this.updateConditionalValidators(dynamicForm, nestedFields, index, hidden);
  }

  private static updateControlValidators(
    control: AbstractControl,
    field: IDynamicFormFieldType,
    dynamicForm: FormGroup,
    formTemplateList: IDynamicFormFieldType[],
    index: number | undefined,
    hidden: boolean
  ): void {
    if (hidden) {
      control.clearValidators();
      control.setErrors(null);

      if (control instanceof FormControl && control.value !== undefined) {
        control.setValue(undefined, {emitEvent: false});
      }
    } else {
      const validations = this.getEffectiveValidators(field, dynamicForm, formTemplateList, index);
      control.setValidators(validations);
    }

    control.updateValueAndValidity({emitEvent: false});
  }

  private static updateConditionalValidatorsForControl(
    control: AbstractControl,
    nestedFields: IDynamicFormFieldType[],
    hidden: boolean
  ): void {
    if (nestedFields.length === 0) {
      return;
    }

    if (control instanceof FormGroup || control instanceof FormArray) {
      this.updateConditionalValidators(control, nestedFields, undefined, hidden);
    }
  }

  private static getNestedFields(field: IDynamicFormFieldType): IDynamicFormFieldType[] {
    const nestedFields = (field as any).fields;
    return Array.isArray(nestedFields) ? nestedFields : [];
  }

  private static getEffectiveValidators(
    field: IDynamicFormFieldType,
    parentGroup: FormGroup,
    formTemplateList: IDynamicFormFieldType[],
    index?: number
  ): ValidatorFn[] {
    const validators = (field.validators ?? []).filter(v => v.restriction !== ERestriction.Required);
    const effectiveField: IDynamicFormFieldType = {
      ...field,
      validators,
    };
    const effectiveValidators = this.buildValidations(effectiveField);

    if (this.isRequired(field, parentGroup, formTemplateList, index)) {
      effectiveValidators.push(Validators.required);
    }

    return effectiveValidators;
  }

  static isRequired(
    formTemplate: IDynamicFormFieldType,
    parentGroup: FormGroup,
    formTemplateList: IDynamicFormFieldType[],
    index?: number
  ) {
    const isFieldRequired = this.getRestriction(formTemplate, ERestriction.Required);
    const isConditionalRequired = this.conditionalValidation(
      formTemplate,
      EConditionType.Required,
      parentGroup,
      formTemplateList,
      index
    );
    const isConditionalOptional = this.conditionalValidation(
      formTemplate,
      EConditionType.Optional,
      parentGroup,
      formTemplateList,
      index
    );
    return this.conditionalValue(isConditionalRequired, isConditionalOptional, isFieldRequired);
  }

  static isDisabled(
    formTemplate: IDynamicFormFieldType,
    parentGroup: FormGroup,
    formTemplateList: IDynamicFormFieldType[],
    index?: number
  ) {
    const isFieldDisabled = !!formTemplate.disabled;
    const isConditionalDisabled = this.conditionalValidation(
      formTemplate,
      EConditionType.Disable,
      parentGroup,
      formTemplateList,
      index
    );
    const isConditionalEnabled = this.conditionalValidation(
      formTemplate,
      EConditionType.Enable,
      parentGroup,
      formTemplateList,
      index
    );
    return this.conditionalValue(isConditionalDisabled, isConditionalEnabled, isFieldDisabled);
  }

  static isHidden(
    formTemplate: IDynamicFormFieldType,
    parentGroup: FormGroup,
    formTemplateList: IDynamicFormFieldType[],
    index?: number
  ) {
    const isFieldHidden = !!formTemplate.hidden;
    const isConditionalHidden = this.conditionalValidation(
      formTemplate,
      EConditionType.Hide,
      parentGroup,
      formTemplateList,
      index
    );
    const isConditionalShown = this.conditionalValidation(
      formTemplate,
      EConditionType.Show,
      parentGroup,
      formTemplateList,
      index
    );
    return this.conditionalValue(isConditionalHidden, isConditionalShown, isFieldHidden);
  }

  static conditionalValue(
    conditionValue: boolean | null,
    conditionOppositeValue: boolean | null,
    initialValue: boolean
  ) {
    if (conditionValue != null || conditionOppositeValue != null) {
      if (conditionValue != null && conditionOppositeValue != null) {
        return conditionValue && !conditionOppositeValue;
      } else if (conditionValue != null) {
        return conditionValue;
      } else {
        return !conditionOppositeValue;
      }
    }
    return initialValue;
  }

  static conditionalValidation(
    formTemplate: IDynamicFormFieldType,
    type: EConditionType,
    parentGroup: FormGroup,
    formTemplateList: IDynamicFormFieldType[],
    index?: number
  ) {
    const conditionalFields = this.getAllFieldsWithTargetName(
      formTemplateList,
      formTemplate.name,
      type,
      parentGroup,
      index
    );
    const isConditionValid = conditionalFields.some(field => {
      return this.checkFieldCondition(field, type, parentGroup, index);
    });
    return conditionalFields?.length ? isConditionValid : null;
  }

  static checkFieldCondition(
    field: IDynamicFormFieldType,
    type: EConditionType,
    parentGroup: FormGroup,
    index?: number
  ) {
    let scopedGroup: FormGroup = parentGroup;
    if (index !== undefined && parentGroup instanceof FormArray) {
      const groupAtIndex = parentGroup.at(index);
      if (groupAtIndex instanceof FormGroup) {
        scopedGroup = groupAtIndex;
      }
    }

    const condition = field.conditions?.find(cond => cond.type === type);
    const formField = scopedGroup.get(field.name);
    const fieldValue = formField?.value;
    const fieldValid = !!formField?.valid;
    if (condition) {
      let valueValid = false;
      if (condition.value) {
        valueValid = Array.isArray(fieldValue) ? fieldValue.includes(condition.value) : fieldValue == condition.value;
      } else {
        valueValid = Array.isArray(fieldValue) ? !!fieldValue.length : !!fieldValue;
      }
      return fieldValid && valueValid;
    }
    return false;
  }

  static getAllFieldsWithTargetName(
    fields: IDynamicFormFieldType[],
    name: string,
    type: EConditionType,
    parentGroup: FormGroup,
    index?: number
  ): IDynamicFormFieldType[] {
    if (!fields?.length) return [];

    const targetFields: IDynamicFormFieldType[] = [];
    for (const field of fields) {
      const fieldConditions = field.conditions?.filter(cond => {
        const targetField = cond.targetFieldName.split('.').pop() ?? '';
        return cond.type === type && name.includes(targetField);
      });

      if (fieldConditions?.length) {
        targetFields.push(field);
      }
      const nestedFields = (field as any).fields;
      if (Array.isArray(nestedFields) && nestedFields.length > 0) {
        targetFields.push(...this.getAllFieldsWithTargetName(nestedFields, name, type, parentGroup, index));
      }
    }
    return targetFields;
  }

  static getControlByName(absControl: AbstractControl, name: string, index?: number): FormControl | null {
    if (absControl instanceof FormArray) {
      return this.getControlFromArray(absControl, name, index);
    }
    if (absControl instanceof FormGroup) {
      return this.getControlFromGroup(absControl, name);
    }
    return null;
  }

  private static getControlFromArray(formArray: FormArray, name: string, index?: number): FormControl | null {
    if (index !== undefined) {
      const controlAtIndex = formArray.at(index);
      if (controlAtIndex instanceof FormGroup) {
        return this.getControlFromGroup(controlAtIndex, name);
      }
    }
    for (const control of formArray.controls) {
      const found = this.getControlByName(control, name);
      if (found) return found;
    }
    return null;
  }

  private static getControlFromGroup(formGroup: FormGroup, name: string): FormControl | null {
    for (const key of Object.keys(formGroup.controls)) {
      if (key === name) {
        return formGroup.get(key) as FormControl;
      }
      const nestedControl = formGroup.get(key);
      if (nestedControl) {
        const found = this.getControlByName(nestedControl, name);
        if (found) return found;
      }
    }
    return null;
  }
}
